import { useEffect, useState } from 'react';
import api from '@/api/axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Lightning, CircleNotch, CopySimple } from '@phosphor-icons/react';

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

export const CargaRapidaMetas = () => {
  const now = new Date();
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async (y = year, m = month) => {
    try {
      setLoading(true);
      const res = await api.get(`/work-plan/goals/team?year=${y}&month=${m}`);
      setRows(res.data.vendors || []);
    } catch (e) {
      toast.error('Error cargando el equipo');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(year, month); }, [open, year, month]); // eslint-disable-line

  const setField = (uid, field, value) => {
    setRows((prev) => prev.map((r) => r.user_id === uid ? { ...r, [field]: value, _dirty: true } : r));
  };

  const copyPrevMonth = async () => {
    const pm = month === 1 ? 12 : month - 1;
    const py = month === 1 ? year - 1 : year;
    try {
      setLoading(true);
      const res = await api.get(`/work-plan/goals/team?year=${py}&month=${pm}`);
      const prev = {};
      (res.data.vendors || []).forEach((v) => { prev[v.user_id] = v; });
      setRows((cur) => cur.map((r) => {
        const p = prev[r.user_id];
        if (!p || !p.has_goal) return r;
        return {
          ...r, _dirty: true,
          target_amount: r.target_amount ?? p.target_amount,
          monthly_calls: r.monthly_calls ?? p.monthly_calls,
          monthly_visits: r.monthly_visits ?? p.monthly_visits,
          monthly_quotes: r.monthly_quotes ?? p.monthly_quotes,
        };
      }));
      toast.success(`Metas de ${MESES[pm - 1]} copiadas (solo en campos vacíos)`);
    } catch (e) {
      toast.error('Error copiando mes anterior');
    } finally { setLoading(false); }
  };

  const saveAll = async () => {
    const dirty = rows.filter((r) => r._dirty);
    if (!dirty.length) { toast.info('No hay cambios que guardar'); return; }
    try {
      setSaving(true);
      const res = await api.put('/work-plan/goals/bulk', {
        year, month,
        goals: dirty.map((r) => ({
          user_id: r.user_id,
          target_amount: r.target_amount,
          monthly_calls: r.monthly_calls,
          monthly_visits: r.monthly_visits,
          monthly_quotes: r.monthly_quotes,
        })),
      });
      toast.success(`✅ ${res.data.saved} meta(s) guardadas para ${MESES[month - 1]} ${year}`);
      load(year, month);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error guardando metas');
    } finally { setSaving(false); }
  };

  const NumInput = ({ row, field, placeholder }) => (
    <input
      type="number"
      min="0"
      value={row[field] ?? ''}
      placeholder={placeholder}
      onChange={(e) => setField(row.user_id, field, e.target.value === '' ? null : Number(e.target.value))}
      data-testid={`meta-input-${field}-${row.user_id}`}
      className="w-24 px-2 py-1.5 text-xs text-right rounded-md bg-[var(--bg)] border border-[var(--border)] text-[var(--text)] focus:border-[var(--primary)] outline-none"
    />
  );

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-500 text-white text-xs font-bold px-3 py-2 rounded-md"
        data-testid="btn-carga-rapida-metas"
      >
        <Lightning size={14} weight="bold" />
        Carga Rápida Metas
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="carga-metas-dialog">
          <DialogHeader>
            <DialogTitle>⚡ Carga Rápida de Metas — todo el equipo</DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="carga-metas-mes"
              className="px-2 py-1.5 text-xs font-bold rounded-md bg-[var(--bg)] border border-[var(--border)] text-[var(--text)]">
              {MESES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))}
              className="px-2 py-1.5 text-xs font-bold rounded-md bg-[var(--bg)] border border-[var(--border)] text-[var(--text)]">
              {[now.getFullYear() + 1, now.getFullYear(), now.getFullYear() - 1].map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <Button size="sm" variant="outline" onClick={copyPrevMonth} disabled={loading} data-testid="carga-metas-copiar">
              <CopySimple size={14} className="mr-1" /> Copiar mes anterior
            </Button>
            <div className="flex-1" />
            <Button size="sm" onClick={saveAll} disabled={saving || loading} data-testid="carga-metas-guardar">
              {saving ? <CircleNotch size={14} className="animate-spin mr-1" /> : null}
              Guardar todo
            </Button>
          </div>
          <p className="text-[11px] text-[var(--text-muted)] mb-2">Las metas diarias se calculan automáticamente (~24 días laborales). Solo se guardan las filas que edites.</p>
          {loading ? (
            <p className="text-sm text-[var(--text-muted)] py-6 text-center">Cargando equipo…</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wide text-[var(--text-faint)] border-b border-[var(--border)]">
                    <th className="text-left py-2 pr-2">Vendedor</th>
                    <th className="text-right py-2">Meta Venta (Q)</th>
                    <th className="text-right py-2">Llamadas/mes</th>
                    <th className="text-right py-2">Visitas/mes</th>
                    <th className="text-right py-2">Cotiz./mes</th>
                    <th className="text-center py-2">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.user_id} className="border-b border-[var(--border)]" data-testid={`carga-metas-row-${r.user_id}`}>
                      <td className="py-2 pr-2">
                        <p className="font-bold text-[var(--text)] text-xs">{r.name}</p>
                        <p className="text-[10px] text-[var(--text-faint)]">{r.branch || 'sin sucursal'}</p>
                      </td>
                      <td className="text-right py-1.5"><NumInput row={r} field="target_amount" placeholder="80000" /></td>
                      <td className="text-right py-1.5"><NumInput row={r} field="monthly_calls" placeholder="250" /></td>
                      <td className="text-right py-1.5"><NumInput row={r} field="monthly_visits" placeholder="40" /></td>
                      <td className="text-right py-1.5"><NumInput row={r} field="monthly_quotes" placeholder="60" /></td>
                      <td className="text-center">
                        {r._dirty
                          ? <span className="text-[10px] font-bold text-amber-500">● sin guardar</span>
                          : r.has_goal
                            ? <span className="text-[10px] font-bold text-emerald-500">✓ con meta</span>
                            : <span className="text-[10px] text-[var(--text-faint)]">sin meta</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CargaRapidaMetas;
